import React, { useState, useEffect } from 'react';
import styles from './ZamModal.module.css';
import { FaPercentage, FaTimes, FaCheck, FaArrowUp } from 'react-icons/fa';
import { useTheme } from '../../../contexts/ThemeContext';
import { collection, query, getDocs, addDoc, where } from 'firebase/firestore';
import { db } from '../../../config/firebaseConfig';
import { toast } from 'react-toastify';

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY'
    }).format(amount).replace('₺', '') + ' ₺';
};

const ZamModal = ({ onClose }) => {
    const { isDarkMode } = useTheme();
    const [employees, setEmployees] = useState([]);
    const [employeeSalaries, setEmployeeSalaries] = useState({});
    const [selectedEmployees, setSelectedEmployees] = useState({});
    const [raisePercentage, setRaisePercentage] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchEmployeesWithSalaries();
    }, []);

    const fetchEmployeesWithSalaries = async () => {
        try {
            setLoading(true);

            // Fetch all employees
            const employeesSnapshot = await getDocs(collection(db, 'Employees'));
            const employeesData = employeesSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Fetch all salaries
            const salariesSnapshot = await getDocs(collection(db, 'Salaries'));
            const salariesData = salariesSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Group salaries by employee and get the latest salary for each
            const salariesByEmployee = {};
            salariesData.forEach(salary => {
                if (!salariesByEmployee[salary.employeeId]) {
                    salariesByEmployee[salary.employeeId] = salary;
                } else {
                    // Compare timestamps (updatedAt or createdAt) and keep the latest
                    const currentTime = salariesByEmployee[salary.employeeId].updatedAt ||
                                       salariesByEmployee[salary.employeeId].createdAt || 0;
                    const newTime = salary.updatedAt || salary.createdAt || 0;
                    if (newTime > currentTime) {
                        salariesByEmployee[salary.employeeId] = salary;
                    }
                }
            });

            // Filter employees who have salaries
            const employeesWithSalaries = employeesData.filter(emp =>
                salariesByEmployee[emp.id] && emp.Active === 1
            );

            setEmployees(employeesWithSalaries);
            setEmployeeSalaries(salariesByEmployee);

            // Initialize all employees as selected
            const initialSelection = {};
            employeesWithSalaries.forEach(emp => {
                initialSelection[emp.id] = true;
            });
            setSelectedEmployees(initialSelection);

            setLoading(false);
        } catch (error) {
            console.error('Error fetching employees with salaries:', error);
            toast.error('Çalışanlar yüklenirken bir hata oluştu');
            setLoading(false);
        }
    };

    const handleToggleEmployee = (employeeId) => {
        setSelectedEmployees(prev => ({
            ...prev,
            [employeeId]: !prev[employeeId]
        }));
    };

    const handleSelectAll = () => {
        const allSelected = {};
        employees.forEach(emp => {
            allSelected[emp.id] = true;
        });
        setSelectedEmployees(allSelected);
    };

    const handleDeselectAll = () => {
        setSelectedEmployees({});
    };

    const calculateNewSalary = (currentSalary) => {
        if (!raisePercentage || isNaN(raisePercentage)) return currentSalary;

        const percentage = parseFloat(raisePercentage);
        const currentNet = parseFloat(currentSalary.netSalary);
        const newNet = currentNet * (1 + percentage / 100);

        return {
            ...currentSalary,
            netSalary: newNet.toFixed(2)
        };
    };

    const calculateGrossFromNet = (targetNet) => {
        if (!targetNet || targetNet <= 0) {
            return {
                grossSalary: 0,
                totalDeductions: 0,
                totalEmployerCost: 0
            };
        }

        let low = targetNet;
        let high = targetNet * 2;
        let grossSalary = (low + high) / 2;
        let iterations = 0;
        const maxIterations = 30;

        while (iterations < maxIterations) {
            const calculations = calculateCosts(grossSalary);
            const currentNet = parseFloat(calculations.netSalary);

            if (Math.abs(currentNet - targetNet) < 0.01) {
                return {
                    grossSalary: grossSalary.toFixed(2),
                    totalDeductions: calculations.totalDeductions,
                    totalEmployerCost: calculations.totalEmployerCost,
                };
            }

            if (currentNet > targetNet) {
                high = grossSalary;
            } else {
                low = grossSalary;
            }

            grossSalary = (low + high) / 2;
            iterations++;
        }

        const finalCalculations = calculateCosts(grossSalary);
        return {
            grossSalary: grossSalary.toFixed(2),
            totalDeductions: finalCalculations.totalDeductions,
            totalEmployerCost: finalCalculations.totalEmployerCost,
        };
    };

    const calculateCosts = (grossSalary) => {
        const sskEmployeeShare = grossSalary * 0.14;
        const unemploymentEmployeeShare = grossSalary * 0.01;
        const stampTax = grossSalary * 0.00759;

        const incomeTaxBase = grossSalary - (sskEmployeeShare + unemploymentEmployeeShare);
        let incomeTax = 0;

        if (incomeTaxBase <= 24000) {
            incomeTax = incomeTaxBase * 0.15;
        } else if (incomeTaxBase <= 53000) {
            incomeTax = 3600 + (incomeTaxBase - 24000) * 0.20;
        } else if (incomeTaxBase <= 190000) {
            incomeTax = 9400 + (incomeTaxBase - 53000) * 0.27;
        } else {
            incomeTax = 46390 + (incomeTaxBase - 190000) * 0.35;
        }

        const totalDeductions = sskEmployeeShare + unemploymentEmployeeShare + incomeTax + stampTax;
        const netSalary = grossSalary - totalDeductions;

        const sskEmployerShare = grossSalary * 0.155;
        const unemploymentEmployerShare = grossSalary * 0.02;
        const totalEmployerCost = grossSalary + sskEmployerShare + unemploymentEmployerShare;

        return {
            netSalary: netSalary.toFixed(2),
            totalDeductions: totalDeductions.toFixed(2),
            totalEmployerCost: totalEmployerCost.toFixed(2),
        };
    };

    const handleApplyRaise = async () => {
        const selectedCount = Object.values(selectedEmployees).filter(Boolean).length;

        if (selectedCount === 0) {
            toast.error('Lütfen en az bir çalışan seçin!');
            return;
        }

        if (!raisePercentage || isNaN(raisePercentage) || parseFloat(raisePercentage) <= 0) {
            toast.error('Lütfen geçerli bir zam yüzdesi girin!');
            return;
        }

        if (!window.confirm(`${selectedCount} çalışan için %${raisePercentage} zam uygulamak istediğinize emin misiniz?`)) {
            return;
        }

        try {
            const today = new Date().toISOString().split('T')[0];
            const timestamp = new Date().getTime(); // Add timestamp for sorting
            let successCount = 0;

            for (const employee of employees) {
                if (selectedEmployees[employee.id]) {
                    const currentSalary = employeeSalaries[employee.id];
                    const newNetSalary = parseFloat(currentSalary.netSalary) * (1 + parseFloat(raisePercentage) / 100);

                    // Calculate new gross salary and other values
                    const calculations = calculateGrossFromNet(newNetSalary);

                    const newSalaryData = {
                        employeeId: employee.id,
                        contractDate: today,
                        netSalary: newNetSalary.toFixed(2),
                        grossSalary: calculations.grossSalary,
                        totalDeductions: calculations.totalDeductions,
                        suggestedCost: calculations.totalEmployerCost,
                        createdAt: timestamp // Add timestamp field
                    };

                    await addDoc(collection(db, 'Salaries'), newSalaryData);
                    successCount++;
                }
            }

            toast.success(`${successCount} çalışan için zam başarıyla uygulandı!`);
            onClose();
        } catch (error) {
            console.error('Error applying raise:', error);
            toast.error('Zam uygulanırken bir hata oluştu!');
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const getNewSalaryPreview = (employeeId) => {
        const currentSalary = employeeSalaries[employeeId];

        // Only show preview if employee is selected
        if (!selectedEmployees[employeeId] || !currentSalary || !raisePercentage || isNaN(raisePercentage)) {
            return null;
        }

        const currentNet = parseFloat(currentSalary.netSalary);
        const newNet = currentNet * (1 + parseFloat(raisePercentage) / 100);
        const difference = newNet - currentNet;

        return {
            current: currentNet,
            new: newNet,
            difference: difference
        };
    };

    return (
        <div className={styles.modalOverlay} onClick={handleOverlayClick}>
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>
                        <FaArrowUp className={styles.modalIcon} />
                        Toplu Maaş Zammı
                    </h2>
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label="Kapat"
                    >
                        <FaTimes />
                    </button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.controlsSection}>
                        <div className={styles.percentageInput}>
                            <label className={styles.label}>
                                <FaPercentage className={styles.labelIcon} />
                                Zam Yüzdesi:
                            </label>
                            <input
                                type="number"
                                value={raisePercentage}
                                onChange={(e) => setRaisePercentage(e.target.value)}
                                placeholder="Örn: 10"
                                className={styles.input}
                                min="0"
                                step="0.1"
                            />
                            <span className={styles.percentSign}>%</span>
                        </div>

                        <div className={styles.selectionButtons}>
                            <button
                                className={styles.selectButton}
                                onClick={handleSelectAll}
                            >
                                Tümünü Seç
                            </button>
                            <button
                                className={styles.deselectButton}
                                onClick={handleDeselectAll}
                            >
                                Tümünü Kaldır
                            </button>
                        </div>
                    </div>

                    <div className={styles.tableContainer}>
                        {loading ? (
                            <div className={styles.loadingMessage}>Yükleniyor...</div>
                        ) : employees.length === 0 ? (
                            <div className={styles.emptyMessage}>
                                Maaş bilgisi olan aktif çalışan bulunamadı.
                            </div>
                        ) : (
                            <table className={styles.employeeTable}>
                                <thead>
                                    <tr>
                                        <th>Seç</th>
                                        <th>Çalışan</th>
                                        <th>Departman</th>
                                        <th>Mevcut Net Maaş</th>
                                        <th>Yeni Net Maaş</th>
                                        <th>Fark</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {employees.map(employee => {
                                        const preview = getNewSalaryPreview(employee.id);
                                        const currentSalary = employeeSalaries[employee.id];

                                        return (
                                            <tr
                                                key={employee.id}
                                                className={selectedEmployees[employee.id] ? styles.selectedRow : ''}
                                            >
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedEmployees[employee.id] || false}
                                                        onChange={() => handleToggleEmployee(employee.id)}
                                                        className={styles.checkbox}
                                                    />
                                                </td>
                                                <td>{employee.eName} {employee.eSurname}</td>
                                                <td>{employee.departman || '-'}</td>
                                                <td className={styles.currentSalary}>
                                                    {formatCurrency(currentSalary.netSalary)}
                                                </td>
                                                <td className={styles.newSalary}>
                                                    {preview ? formatCurrency(preview.new) : formatCurrency(currentSalary.netSalary)}
                                                </td>
                                                <td className={styles.difference}>
                                                    {preview ? (
                                                        <span className={styles.differenceAmount}>
                                                            +{formatCurrency(preview.difference)}
                                                        </span>
                                                    ) : '-'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    <button
                        className={styles.cancelButton}
                        onClick={onClose}
                    >
                        İptal
                    </button>
                    <button
                        className={styles.applyButton}
                        onClick={handleApplyRaise}
                        disabled={loading || employees.length === 0}
                    >
                        <FaCheck className={styles.buttonIcon} />
                        Zammı Uygula
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ZamModal;
